import { ICE_LEVELS, MAX_TOPPINGS, SUGAR_LEVELS, type IceLevel, type ProductView, type Size, type SugarLevel } from '@khaddi/contracts';
import { Button, colors, Cup, ErrorNote, FooterBar, formatVnd, Icon, Screen, Spinner, Stepper, Text, useLayout, useRouter } from '@khaddi/ui';
import { useState, type ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { OptionPill } from '../components/bits';
import { useTabNav } from '../components/TabNav';
import { useFeatures } from '../state/appConfig';
import { useCart } from '../state/cart';
import { useCatalog } from '../state/catalog';
import { useFavourite } from '../state/favourites';

const DEFAULT_SUGAR: SugarLevel = '70%';
const DEFAULT_ICE: IceLevel = 'Bình thường';

export const ProductScreen = ({ productId }: { productId: string }) => {
  const { catalog, loading, error, reload } = useCatalog();
  const { navigate } = useRouter();
  if (loading) return <Spinner />;
  if (error && !catalog) return <ErrorNote message={error} onRetry={() => void reload()} />;
  const product = catalog?.drinks.find((d) => d.id === productId);
  if (!catalog || !product) {
    return (
      <View style={{ padding: 16, gap: 12 }}>
        <ErrorNote message="Không tìm thấy món này, có thể món đã ngừng bán." />
        <Button label="Về thực đơn" variant="outline" onPress={() => navigate('/menu', { replace: true })} />
      </View>
    );
  }
  return <ProductDetail product={product} toppings={catalog.toppings.filter((t) => t.available && t.visibleOnApp)} />;
};

const ProductDetail = ({ product, toppings }: { product: ProductView; toppings: ProductView[] }) => {
  const { navigate, back } = useRouter();
  const { addLine } = useCart();
  const { onlineOrdering } = useFeatures();
  const { favourite, toggle } = useFavourite(product.id);
  const { wide } = useLayout();
  const nav = useTabNav('menu', { wideOnly: true });
  const groups = new Set(product.optionGroups);
  const hasLarge = groups.has('size') && product.priceL !== null;

  const [size, setSize] = useState<Size>('M');
  const [sugar, setSugar] = useState<SugarLevel>(DEFAULT_SUGAR);
  const [ice, setIce] = useState<IceLevel>(DEFAULT_ICE);
  const [toppingIds, setToppingIds] = useState<string[]>([]);
  const [quantity, setQuantity] = useState(1);

  const chosenToppings = toppings.filter((t) => toppingIds.includes(t.id));
  const basePrice = size === 'L' && product.priceL !== null ? product.priceL : product.priceM;
  const unitPrice = basePrice + chosenToppings.reduce((sum, t) => sum + t.priceM, 0);
  const total = unitPrice * quantity;

  const toggleTopping = (id: string) =>
    setToppingIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : ids.length < MAX_TOPPINGS ? [...ids, id] : ids));

  const add = () => {
    const optionsLabel = [`Size ${size}`, groups.has('sugar') ? `${sugar} ngọt` : null, groups.has('ice') && ice !== DEFAULT_ICE ? ice : null, ...chosenToppings.map((t) => t.name)]
      .filter(Boolean)
      .join(' · ');
    addLine({ productId: product.id, size, sugar, ice, toppingIds, quantity, name: product.name, tint: product.tint, background: product.background, unitPrice, optionsLabel });
    navigate('/checkout');
  };

  // On wide screens the add-to-cart bar sits under the options instead of sticking to the bottom.
  const ctaStyle = wide ? { borderTopWidth: 0, paddingHorizontal: 0, paddingBottom: 0, backgroundColor: 'transparent' } : null;
  const cta = (
    onlineOrdering ? (
      <FooterBar style={[{ flexDirection: 'row', alignItems: 'center', gap: 12 }, ctaStyle]}>
        <Stepper value={quantity} onChange={setQuantity} />
        <Button
          label={product.available ? `Thêm vào giỏ · ${formatVnd(total)}` : 'Món tạm hết'}
          disabled={!product.available}
          onPress={add}
          style={{ flex: 1, paddingHorizontal: 10 }}
        />
      </FooterBar>
    ) : (
      <FooterBar style={ctaStyle}>
        <Text variant="caption" style={{ textAlign: 'center' }}>
          {product.available ? `${formatVnd(total)} · Order tại quầy, đọc số điện thoại để tích điểm` : 'Món tạm hết'}
        </Text>
      </FooterBar>
    )
  );
  const hero = (
    <View style={[{ height: 270, backgroundColor: product.background, alignItems: 'center', justifyContent: 'flex-end' }, wide ? { height: 440, borderRadius: 24, overflow: 'hidden' } : null]}>
      <HeroButton label="Quay lại" onPress={() => back('/')} side="left">
        <Icon name="chevronLeft" size={22} color={colors.text} strokeWidth={2.2} />
      </HeroButton>
      <HeroButton label={favourite ? 'Bỏ yêu thích' : 'Yêu thích'} onPress={toggle} side="right" pressed={favourite}>
        <Icon name="heart" size={22} color={colors.red} filled={favourite} />
      </HeroButton>
      <View style={{ marginBottom: -10 }}>
        <Cup tint={product.tint} width={wide ? 240 : 168} pearls={4} shine imageUrl={product.imageUrl} />
      </View>
    </View>
  );
  const details = (
    <View style={wide ? { gap: 16 } : { marginTop: -18, backgroundColor: colors.surface, borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingTop: 20, paddingHorizontal: 20, gap: 16 }}>
      <View>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <Text variant="display" style={{ fontSize: 26, lineHeight: 30, flex: 1 }}>
            {product.name}
          </Text>
          <Text variant="title" color={colors.primaryText} numeric>
            {formatVnd(basePrice)}
          </Text>
        </View>
        {product.description ? (
          <Text variant="caption" style={{ marginTop: 4, fontSize: 13, lineHeight: 19.5 }}>
            {product.description}
          </Text>
        ) : null}
        <View style={{ marginTop: 8, alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: colors.goldSoft, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999 }}>
          <Icon name="star" size={14} color={colors.goldDeep} filled />
          <Text variant="label" color={colors.goldText} style={{ fontSize: 12 }}>
            +{Math.floor(total / 1000)} điểm cho món này
          </Text>
        </View>
      </View>

      {groups.has('size') ? (
        <OptionGroup title="Size">
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <SizeOption label="Size M" extra="Tiêu chuẩn" selected={size === 'M'} onPress={() => setSize('M')} />
            {hasLarge && product.priceL !== null ? (
              <SizeOption label="Size L" extra={`+${formatVnd(product.priceL - product.priceM)}`} selected={size === 'L'} onPress={() => setSize('L')} />
            ) : null}
          </View>
        </OptionGroup>
      ) : null}

      {groups.has('sugar') ? (
        <OptionGroup title="Độ ngọt">
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {SUGAR_LEVELS.map((level) => (
              <OptionPill key={level} label={level} selected={sugar === level} onPress={() => setSugar(level)} style={{ flex: 1, paddingHorizontal: 0 }} />
            ))}
          </View>
        </OptionGroup>
      ) : null}

      {groups.has('ice') ? (
        <OptionGroup title="Đá">
          <View style={{ flexDirection: 'row', gap: 6 }}>
            {ICE_LEVELS.map((level) => (
              <OptionPill key={level} label={level} selected={ice === level} onPress={() => setIce(level)} style={{ flex: 1 }} />
            ))}
          </View>
        </OptionGroup>
      ) : null}

      {groups.has('topping') && toppings.length ? (
        <OptionGroup
          title="Topping"
          hint={`(tối đa ${MAX_TOPPINGS})`}
        >
          <View>
            {toppings.map((t) => {
              const on = toppingIds.includes(t.id);
              const blocked = !on && toppingIds.length >= MAX_TOPPINGS;
              return (
                <Pressable
                  key={t.id}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: on, disabled: blocked }}
                  disabled={blocked}
                  onPress={() => toggleTopping(t.id)}
                  style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 48, borderBottomWidth: 1, borderBottomColor: colors.divider, opacity: blocked ? 0.45 : 1 }}
                >
                  <View style={{ width: 22, height: 22, borderRadius: 7, borderWidth: 2, borderColor: on ? colors.primaryText : colors.inputBorder, backgroundColor: on ? colors.primary : colors.surface, alignItems: 'center', justifyContent: 'center' }}>
                    {on ? <Icon name="check" size={14} color={colors.onPrimary} strokeWidth={3.5} /> : null}
                  </View>
                  <Text style={{ flex: 1 }}>{t.name}</Text>
                  <Text color={colors.textMuted} style={{ fontWeight: '400' }} numeric>
                    +{formatVnd(t.priceM)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </OptionGroup>
      ) : null}
    </View>
  );

  if (!wide) {
    return (
      <Screen background={colors.surface} footer={cta} nav={nav}>
        {hero}
        {details}
      </Screen>
    );
  }
  return (
    <Screen background={colors.surface} nav={nav}>
      <View style={{ flexDirection: 'row', gap: 32, padding: 24, alignItems: 'flex-start' }}>
        <View style={{ flex: 1, minWidth: 0, position: 'sticky' as unknown as 'relative', top: 24 }}>{hero}</View>
        <View style={{ flex: 1, minWidth: 0, gap: 16 }}>
          {details}
          {cta}
        </View>
      </View>
    </Screen>
  );
};

const HeroButton = ({ label, onPress, side, children, pressed }: { label: string; onPress: () => void; side: 'left' | 'right'; children: ReactNode; pressed?: boolean }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityLabel={label}
    aria-pressed={pressed}
    onPress={onPress}
    style={{ position: 'absolute', top: 16, ...(side === 'left' ? { left: 16 } : { right: 16 }), width: 44, height: 44, borderRadius: 22, backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}
  >
    {children}
  </Pressable>
);

const OptionGroup = ({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) => (
  <View role="group" aria-label={title} style={{ gap: 8 }}>
    <Text variant="heading">
      {title}
      {hint ? (
        <Text variant="caption" style={{ fontSize: 13 }}>
          {' '}
          {hint}
        </Text>
      ) : null}
    </Text>
    {children}
  </View>
);

const SizeOption = ({ label, extra, selected, onPress }: { label: string; extra: string; selected: boolean; onPress: () => void }) => (
  <Pressable
    accessibilityRole="button"
    accessibilityState={{ selected }}
    aria-pressed={selected}
    onPress={onPress}
    style={{ flex: 1, height: 52, borderRadius: 14, borderWidth: 2, borderColor: selected ? colors.primaryText : colors.border, backgroundColor: selected ? colors.surfaceMuted : colors.surface, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 14 }}
  >
    <Text style={{ fontWeight: '600' }}>{label}</Text>
    <Text color={colors.textMuted}>{extra}</Text>
  </Pressable>
);
